import axios from 'axios';
import { LoginCredentials, RegisterCredentials } from '../types/auth';
import { User } from '../types/user';
import { WaterMeter, MeterReading } from '../types/waterMeter';
import { FaultReport } from '../types/faultReport';

const API_URL = process.env.REACT_APP_API_URL || 'https://adminvodovod.ddsoft.app/api';
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
  transport: {
    // Force HTTP/1.1
    httpVersion: '1.1'
  }
});
// Add request interceptor to attach the CSRF token
api.interceptors.request.use(async (config) => {
    // Get CSRF cookie first if it doesn't exist
    if (!localStorage.getItem('token') && !document.cookie.includes('XSRF-TOKEN')) {
        await axios.get(`${API_ORIGIN}/sanctum/csrf-cookie`, {
            withCredentials: true
        });
    }
    return config;
});

api.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            // Clear local storage/redux state
            localStorage.removeItem('user');
            // Redirect to login
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth Service
export const authService = {
  login: (credentials: LoginCredentials) => 
    api.post<{ token: string; user: User }>('/login', credentials),
  
  register: (data: RegisterCredentials) => 
    api.post<{ token: string; user: User }>('/register', data),
  
  logout: () => 
    api.post('/logout'),
  
  getCurrentUser: () => 
    api.get<User>('/user'),
};

// Water Meter Service
export const waterMeterService = {
  getAllMeters: () => 
    api.get<WaterMeter[]>('/meters'),
  
  getMeter: (id: number) => 
    api.get<WaterMeter>(`/meters/${id}`),
  
  getReadings: () => 
    api.get<MeterReading[]>('/readings'),
  
  getLatestReading: () => 
    api.get<MeterReading>('/readings/latest'),
  
  importReadings: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/readings/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Fault Report Service
export const faultReportService = {
  getAll: () => 
    api.get<FaultReport[]>('/fault-reports'),
  
  getOne: (id: number) => 
    api.get<FaultReport>(`/fault-reports/${id}`),
  
  create: (data: Partial<FaultReport>) => 
    api.post<FaultReport>('/fault-reports', data),
  
  update: (id: number, data: Partial<FaultReport>) => 
    api.put<FaultReport>(`/fault-reports/${id}`, data),
  
  delete: (id: number) => 
    api.delete(`/fault-reports/${id}`),
};

// User Service
export const userService = {
  getAll: () => 
    api.get<User[]>('/users'),
  
  getOne: (id: number) => 
    api.get<User>(`/users/${id}`),
  
  create: (data: Partial<User>) => 
    api.post<User>('/users', data),
  
  update: (id: number, data: Partial<User>) => 
    api.put<User>(`/users/${id}`, data),
  
  delete: (id: number) => 
    api.delete(`/users/${id}`),
  
  approve: (userId: number) => 
    api.post(`/users/${userId}/approve`),
};

export default api;
